/**
 * Gewählte Menge eines Rezepts: Portionen, Backform und Einheiten.
 *
 * Der Zustand steht in der Adresszeile (?menge=6&form=rund-28&einheiten=gewicht),
 * damit er beim Wechsel in den Kochmodus erhalten bleibt und sich teilen lässt.
 */
import { panFactor, panFromParam, panToParam, parsePan } from '@cookbook/domain';
import type { PanSize, UnitMode } from '@cookbook/domain';
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { RecipeDetail } from '../api/types.ts';

/** Namen der Einheiten-Arten in der Adresszeile. */
const UNIT_PARAMS: Readonly<Record<UnitMode, string>> = {
  original: '',
  weight: 'gewicht',
  volume: 'volumen',
};

/** Ergebnis des Mengen-Zustands. */
export interface ScalingState {
  /** Faktor für alle Mengen im Verhältnis zum Rezept. */
  readonly factor: number;
  /** Gewünschte Menge in der Bezugsgröße des Rezepts. */
  readonly amount: number;
  /** Form im Rezept, falls erkennbar. */
  readonly basePan: PanSize | null;
  /** Eigene Form; null heißt: wie im Rezept. */
  readonly pan: PanSize | null;
  readonly unitMode: UnitMode;
  /** Weicht etwas vom Rezept ab? */
  readonly changed: boolean;
  /** Suchteil der Adresse, zum Weitergeben an den Kochmodus. */
  readonly search: string;
  setAmount: (amount: number) => void;
  setPan: (pan: PanSize | null) => void;
  setUnitMode: (mode: UnitMode) => void;
  reset: () => void;
}

/** Was vom Rezept gebraucht wird. */
type ScalableRecipe = Pick<RecipeDetail, 'baseAmount' | 'baseUnit' | 'baseUnitDetail'>;

/** Zustand der Mengen für ein Rezept. */
export function useScaling(recipe: ScalableRecipe | null): ScalingState {
  const [params, setParams] = useSearchParams();
  const baseAmount = Math.max(1, recipe?.baseAmount ?? 1);
  const basePan = useMemo(() => (recipe === null ? null : parsePan(recipe.baseUnitDetail ?? '')), [recipe]);

  const requested = Number(params.get('menge'));
  const amount = Number.isFinite(requested) && requested > 0 ? Math.min(999, requested) : baseAmount;
  const pan = basePan === null ? null : panFromParam(params.get('form'));
  const unitParam = params.get('einheiten');
  const unitMode: UnitMode =
    unitParam === 'gewicht' ? 'weight' : unitParam === 'volumen' ? 'volume' : 'original';
  const factor = (amount / baseAmount) * (pan !== null && basePan !== null ? panFactor(basePan, pan) : 1);

  /** Ändert einen Wert der Adresszeile, ohne einen Verlaufseintrag anzulegen. */
  const update = useCallback(
    (name: string, value: string | null) => {
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          if (value === null || value === '') {
            next.delete(name);
          } else {
            next.set(name, value);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const search = (() => {
    const next = new URLSearchParams();
    for (const name of ['menge', 'form', 'einheiten']) {
      const value = params.get(name);
      if (value !== null) {
        next.set(name, value);
      }
    }
    const text = next.toString();
    return text === '' ? '' : `?${text}`;
  })();

  return {
    factor,
    amount,
    basePan,
    pan,
    unitMode,
    changed: amount !== baseAmount || pan !== null || unitMode !== 'original',
    search,
    setAmount: (value) =>
      update('menge', value === baseAmount ? null : String(Math.max(1, Math.round(value)))),
    setPan: (value) => update('form', value === null ? null : panToParam(value)),
    setUnitMode: (mode) => update('einheiten', UNIT_PARAMS[mode]),
    reset: () => {
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.delete('menge');
          next.delete('form');
          next.delete('einheiten');
          return next;
        },
        { replace: true },
      );
    },
  };
}
